import { Eye, EyeOff, Lock, User } from "lucide-react";
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Spinner } from "../components/ui/spinner";
import useAuthStore from "../stores/authStore";

const LoginPage = () => {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [redirectUrl, setRedirectUrl] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuthStore();

  // URLパラメータからリダイレクト先とエラーを取得
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const redirect = params.get('redirect');
    const error = params.get('error');
    
    if (redirect) {
      setRedirectUrl(redirect);
    }
    
    if (error) {
      setError(decodeURIComponent(error));
    }
  }, [location]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // SSOリダイレクトログイン時はfetch APIを使用（CSPエラー回避）
      if (redirectUrl) {
        const response = await fetch('/api/auth/login', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...formData,
            redirect: redirectUrl
          }),
          credentials: 'include'
        });

        if (response.ok) {
          const result = await response.json();
          if (result.redirectUrl) {
            console.log("Redirecting to:", result.redirectUrl);
            window.location.href = result.redirectUrl;
          } else {
            navigate('/dashboard');
          }
        } else {
          const errorData = await response.json();
          console.error("Login error:", errorData);
          setError(errorData.error || "ログインに失敗しました");
        }
        return;
      }

      // 通常の直接ログイン時はauthStoreのlogin関数を使用
      const result = await login(formData);
      
      if (result.success) {
        // ダッシュボードへ遷移
        navigate('/dashboard');
      } else {
        setError(result.error || "ログインに失敗しました。ユーザー名とパスワードを確認してください。");
      }
      
    } catch (err) {
      setError("ログインに失敗しました。ユーザー名とパスワードを確認してください。");
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = () => {
    setLoading(true);
    // Microsoft認証エンドポイントにリダイレクト
    window.location.href = '/api/auth/microsoft';
  };

  const handleGoogleLogin = () => {
    setLoading(true);
    setError(""); // 以前のエラーをクリアする
    
    try {
      console.log("Google login initiated");
      // window.openを使ってテストし、ルーティングの衝突を回避する
      const googleAuthUrl = '/api/auth/google';
      console.log("Redirecting to:", googleAuthUrl);
      
      // replaceを使って強制リダイレクトし、React Routerをバイパスする
      window.location.replace(googleAuthUrl);
      
    } catch (error) {
      console.error("Google login error:", error);
      setError("Googleログインの開始に失敗しました");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-2">
          <img
            src="/brand/arjin-logo-horizontal.svg"
            alt="ARJIN Portal"
            className="mx-auto h-10 w-auto"
          />
          <CardTitle className="sr-only">ARJIN</CardTitle>
          <CardDescription>
            {redirectUrl ? "他システムからの認証" : "統一認証プラットフォームにログイン"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* 推奨のMicrosoft認証セクション */}
          <div className="mb-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-center mb-2">
                <svg className="w-5 h-5 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                </svg>
                <span className="text-blue-700 font-medium text-sm">推奨認証方法</span>
              </div>
              <p className="text-blue-600 text-sm">
                Microsoftアカウントを使用すると、セキュアで簡単にログインできます
              </p>
            </div>
            
            <Button
              type="button"
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg"
              onClick={handleMicrosoftLogin}
              disabled={loading}
            >
              <img
                src="/microsoft.svg"
                alt="Microsoftアカウントでログイン"
                width={20}
                height={20}
                className="mr-2"
              />
              Microsoftアカウントでログイン（推奨）
            </Button>

            <Button
              type="button"
              className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white shadow-lg mt-3"
              onClick={handleGoogleLogin}
              disabled={loading}
            >
              <svg
                className="w-5 h-5 mr-2"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Googleアカウントでログイン
            </Button>
          </div>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-2 text-gray-500">
                または従来の方法
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="username" className="text-sm font-medium">
                ユーザー名
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="username"
                  name="username"
                  type="text"
                  value={formData.username}
                  onChange={handleInputChange}
                  placeholder="ユーザー名を入力"
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium">
                パスワード
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="パスワードを入力"
                  className="pl-10 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
                {error}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <div className="flex items-center space-x-2">
                  <Spinner size="sm" />
                  <span>ログイン中...</span>
                </div>
              ) : (
                redirectUrl ? "認証して続行" : "ログイン"
              )}
            </Button>
          </form>

          {process.env.NODE_ENV !== "production" && (
            <div className="mt-6 text-center text-sm text-muted-foreground">
              <p>デフォルトアカウント:</p>
              <p className="font-mono text-xs mt-1">admin / admin123</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
